import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from 'react';
import { useCookies } from 'next-client-cookies';
import { UIProduct } from 'components/commercetools-ui/organisms/product/product-details/types';

export const COOKIE_KEYS = {
  SELECTED_P2R_STORE_NAME: 'channelName',
  ESTIMATED_TIME: 'estimatedTime',
} as const;

type CookieOptions = {
  path?: string;
  sameSite?: 'lax' | 'strict' | 'none';
  secure?: boolean;
  maxAge?: number;
  expires?: Date;
  partitioned?: boolean;
};

export const defaultCookieOptions: CookieOptions = {
  path: '/',
  sameSite: 'none',
  partitioned: true,
  secure: typeof window !== 'undefined' ? window.location.protocol === 'https:' : true,
};

export function endOfDayExpires(): Date {
  const now = new Date();
  const end = new Date(now);
  end.setHours(23, 59, 59, 999);
  return end;
}

interface ChildProps {
  children: ReactNode;
}

type CartLineItem = {
  productCode: string;
  quantity: number;
  mediaFileCount: number;
}

interface StoreFinderContextType {
  productId: string | number | null;
  productCode: string | null;
  selectedP2rStoreName?: string;
  estimatedTime?: string;
  storeFinderModalOpen: boolean;
  m2hChannel?: string
  channelKey?: string
  cartLineItems: CartLineItem[];
  isPriceChangeModalVisible: boolean;
  isStoreChanged: boolean;
  productDetail: UIProduct | undefined;

  setStoreFinderModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setProductId: React.Dispatch<React.SetStateAction<string | number>>;
  setProductDetail: React.Dispatch<React.SetStateAction<UIProduct | undefined>>;
  setProductCode: React.Dispatch<React.SetStateAction<string>>;
  setEstimatedTime: React.Dispatch<React.SetStateAction<string>>;
  setSelectedP2rStoreName: React.Dispatch<React.SetStateAction<string>>;
  setM2hChannel?: React.Dispatch<React.SetStateAction<string>>;
  setChannelKey?: React.Dispatch<React.SetStateAction<string>>;
  setCartLineItems: React.Dispatch<React.SetStateAction<CartLineItem[]>>;
  setIsPriceChangeModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setIsStoreChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

const StoreFinderContext = createContext<StoreFinderContextType | undefined>(undefined);

export const StoreFinderProvider = ({ children }: ChildProps) => {
  const cookies = useCookies();

  const [storeFinderModalOpen, setStoreFinderModalOpen] = useState<boolean>(false);
  const [productId, setProductId] = useState<string | number>('');
  const [productDetail, setProductDetail] = useState<UIProduct | undefined>();
  const [productCode, setProductCode] = useState<string>('');
  const [cartLineItems, setCartLineItems] = useState<CartLineItem[]>([]);
  const [isPriceChangeModalVisible, setIsPriceChangeModalVisible] = useState<boolean>(false);

  const initialSelectedP2rStoreName =
    typeof window !== 'undefined' ? cookies.get(COOKIE_KEYS.SELECTED_P2R_STORE_NAME) || '' : '';
  const initialEstimatedTime =
    typeof window !== 'undefined' ? cookies.get(COOKIE_KEYS.ESTIMATED_TIME) || '' : '';

  const [selectedP2rStoreName, setSelectedP2rStoreName] = useState<string>(initialSelectedP2rStoreName);
  const [m2hChannel, setM2hChannel] = useState<string>('');
  const [channelKey, setChannelKey] = useState<string>('');
  const [estimatedTime, setEstimatedTime] = useState<string>(initialEstimatedTime);
  const [isStoreChanged, setIsStoreChanged] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      cookies.set(COOKIE_KEYS.SELECTED_P2R_STORE_NAME, selectedP2rStoreName ?? '', {
        ...defaultCookieOptions,
      });
    } catch (err) {
      console.warn('Setting selected P2R store name cookie failed:', err);
    }
  }, [selectedP2rStoreName, cookies]);


  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      cookies.set(COOKIE_KEYS.ESTIMATED_TIME, estimatedTime ?? '', {
        ...defaultCookieOptions,
      });
    } catch (err) {
      console.warn('Setting estimatedTime cookie failed:', err);
    }
  }, [estimatedTime, cookies]);


  const value = useMemo<StoreFinderContextType>(
    () => ({
      productId,
      productCode,
      storeFinderModalOpen,
      selectedP2rStoreName,
      estimatedTime,
      setStoreFinderModalOpen,
      setProductId,
      setProductCode,
      setEstimatedTime,
      setSelectedP2rStoreName,
      m2hChannel,
      setM2hChannel,
      channelKey,
      setChannelKey,
      cartLineItems,
      setCartLineItems,
      isPriceChangeModalVisible,
      setIsPriceChangeModalVisible,
      isStoreChanged,
      setIsStoreChanged,
      productDetail,
      setProductDetail,
    }),
    [
      productDetail,
      productId,
      productCode,
      storeFinderModalOpen,
      selectedP2rStoreName,
      estimatedTime,
      m2hChannel,
      channelKey,
      cartLineItems,
      isPriceChangeModalVisible,
      isStoreChanged,
    ]
  );

  return (
    <StoreFinderContext.Provider
      value={value}
    >
      {children}
    </StoreFinderContext.Provider>
  );
};

export const useStoreFinder = () => {
  const context = useContext(StoreFinderContext);
  if (!context) {
    throw new Error('useStoreFinder must be used within a StoreFinderProvider');
  }
  return context;
};
